<template lang="pug">
    div
        v-snackbar(
                v-model='snackbar.show'
                :color='snackbar.color'
                :timeout="snackbar.timeout"
                top
            )
                | {{ snackbar.text }}
                v-btn(dark text @click='snackbar.show = false')
                    | Close
        v-card.vin-login-modal
            v-card-title Vin Solutions Login
                template(v-if="!isLoggingIn")
                    template(v-if="vinLoginStatus == 'unenabled'")
                        v-card-text.vin-login-body
                            p.word-break-normal Vin Solutions Integration is not enabled for your account. Your manager can enable it in the Users admin page.
                    template(v-else-if="vinLoginStatus == 'online'")
                        v-card-text.vin-login-body.text-center
                            v-icon.text-center(color="success" size="100") mdi-check-circle
                            p Vin Solutions Integration is enabled for your account and you are logged in. No further action is needed.
                    template(v-else-if="newMfaCodeRequired")
                        v-card-text.vin-login-body
                            p It looks like we need new credentials or a new MFA code to continue with your Vin Solutions integration. Please click the Login button below to get started.
                    template(v-else-if="!mfaRequired")
                        v-card-text.vin-login-body
                            v-alert.vin-login-alert(type="warning" outlined dense v-if="renderIgnoredEnforcementAlert")
                                | {{ ignoredEnforcementMessage }}
                            template(v-if="isCheckingIntegrationUser")
                                p.vin-login-copy Checking your saved Vin Solutions credentials...
                            template(v-else-if="timeoutOrError")
                                v-alert.vin-login-alert(type="error" outlined dense) Something unexpected happened when logging you into Vin Solutions. Please try again or contact Volie support.
                            template(v-else-if="sessionEnded")
                                v-alert.vin-login-alert(type="warning" outlined dense) Your VIN Solutions session has ended.
                                template(v-if="credentialsRequired")
                                    p.vin-login-copy Please log in with your Vin Solutions credentials to reconnect.
                                template(v-else)
                                    p.vin-login-copy Click Login to reconnect using your saved Vin Solutions credentials.
                                v-alert.vin-login-alert.vin-login-secondary-alert(v-if="renderAcknowledgeButton" type="warning" outlined dense)
                                    | If you continue without reconnecting, VinSolutions activity must be entered manually.
                                p.vin-login-helper(v-if="renderAcknowledgeButton")
                                    | You may need to reconnect if your VinSolutions session expires, requires MFA, times out after extended idle activity, or if you log into VinSolutions in another browser.
                            template(v-else)
                                template(v-if="credentialsRequired")
                                    p.vin-login-copy Please log in with your Vin Solutions credentials to enable Vin Solutions integrations.
                                    p.vin-login-copy If MFA/2FA is required, you will be prompted after clicking Login.
                                template(v-else)
                                    p.vin-login-copy Click Login to reconnect your Vin Solutions session.
                                    p.vin-login-copy If MFA/2FA is required, you will be prompted after clicking Login.
                                v-alert.vin-login-alert(v-if="failedLogin" type="error" outlined) Something went wrong when logging you into Vin Solutions. Check your credentials and try again.
                                v-alert.vin-login-alert.vin-login-secondary-alert(v-if="renderAcknowledgeButton" type="warning" outlined dense)
                                    | If you continue without reconnecting, VinSolutions activity must be entered manually.
                                p.vin-login-helper(v-if="renderAcknowledgeButton")
                                    | You may need to reconnect if your VinSolutions session expires, requires MFA, times out after extended idle activity, or if you log into VinSolutions in another browser.
                            v-form(autocomplete="off" ref="loginForm")
                                v-row(v-if="credentialsRequired")
                                    v-col(cols="12")
                                        v-text-field(
                                            v-model="loginForm.username"
                                            label="Vin Solutions Username"
                                            required
                                            autocomplete="new-password"
                                        )
                                        v-text-field(
                                            v-model="loginForm.password"
                                            label="Vin Solutions Password"
                                            required
                                            type="password"
                                            autocomplete="new-password"
                                        )
                    template(v-else-if="mfaRequired")
                        v-card-text.vin-login-body
                            p.vin-login-copy Please enter your MFA code.
                            v-alert.vin-login-alert(v-if="failedMfa" type="error" outlined) Invalid MFA code
                            v-form(autocomplete="off" ref="mfaForm")
                                v-row
                                    v-col(cols="12")
                                        v-text-field(
                                            v-model="loginForm.mfa_code"
                                            label="MFA Code"
                                            required
                                            autocomplete="new-password"
                                        )

                    v-card-actions.vin-login-actions
                        v-spacer
                        v-btn(v-if="!vinLoginDecisionRequired" ripple outlined color="primary" @click="$emit('close-dialog'); isLoggingIn = false;")
                            v-icon mdi-close
                            | Close
                        v-btn(v-if="renderLoginButton" ripple color="success" @click="handleSubmit()" :disabled="loginButtonDisabled")
                            v-icon mdi-login
                            | {{ loginButtonText }}
                        v-btn(v-if="renderAcknowledgeButton" ripple outlined color="warning" @click="acknowledgeVinLoginEnforcement")
                            v-icon mdi-alert
                            | Ignore and Continue

                template(v-else)
                    v-card-text.vin-login-body.text-center
                        .loading-container.py-4
                            span.text-subtitle-1.mb-4.d-block {{ currentStepText }}
                            v-progress-linear(
                                indeterminate
                                color="primary"
                                rounded
                                height="4"
                                class="mx-auto"
                                style="max-width: 240px"
                            )

                    v-card-actions.vin-login-actions
                        v-spacer
                        v-btn(v-if="!vinLoginDecisionRequired" ripple outlined color="primary" @click="$emit('close-dialog');")
                            v-icon mdi-close
                            | Close
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState from '@/store';

// Components

// Data
import { AcknowledgeVinLoginEnforcement, GetIntegrationUser, CreateIntegrationUser, IntegrationsLogin, IntegrationsSubmitMfa } from '@/data';

export default Vue.extend({
    data() {
        return {
            authStatus: null,
            bus: VolieState.websocketClient().eventBus(),
            createUser: false,
            failedLogin: false,
            failedMfa: false,
            hasIntegrationUser: null,
            isLoggingIn: false,
            loginForm: {
                username: '',
                password: '',
                mfa_code: '',
            },
            mfaRequired: false,
            newMfaCodeRequired: false,
            sessionEnded: false,
            snackbar: {
                    show: false,
                    timeout: 2500,
                },
            steps: [
                {
                    key: "auth.login_attempt",
                    value: "Logging you in..."
                },
                {
                    key: "auth.mfa_verification",
                    value: "Checking if MFA is required..."
                },
                {
                    key: "mfa_submitted",
                    value: "Submitting your MFA code..."
                },
                {
                    key: "auth.success",
                    value: "You're logged in! Vin Solutions will receive actions from your account."
                },
            ],
            timeoutOrError: false,
        }
    },
    methods: {
        acknowledgeVinLoginEnforcement() {
            AcknowledgeVinLoginEnforcement().then((response) => {
                if (response.Error) {
                    this.showSnackbar("error", response.Error);
                    return;
                }
                this.updateVinLoginEnforcement(response.Data?.vin_login_enforcement);
                this.$emit('close-dialog');
                this.showSnackbar("warning", "VinSolutions activity will need to be entered manually until you log in.");
            });
        },
        createIntegrationUser() {
            const options = {
                username: this.loginForm.username,
                password: this.loginForm.password,
                skipLogin: false,
                update_user: this.failedLogin,
            }

            this.isLoggingIn = true;
            CreateIntegrationUser(options).then((response) => {
                if (response.Data?.status !== 200 || response.Error) {
                    let err = "Something went wrong, please try again later";
                    if (response.Error) {
                        err = response.Error;
                    }

                    this.showSnackbar("error", err);
                    this.isLoggingIn = false;
                } else {
                    this.createUser = false;
                    this.failedLogin = false;
                    this.hasIntegrationUser = true;
                }
            });
        },
        getIntegrationUser() {
            // Check if the user is actually enabled for Vin Solutions
            if (!this.currentUser?.need_vin_solutions_auth || !this.currentUser?.vin_login_enabled) {
                return;
            }

            if (this.lastAuthSuccessful) {
                this.hasIntegrationUser = true;
                this.createUser = false;
            }

            // Uses ctx to check if the user is logged in for Integration App, no need to pass anything
            if (!this.lastAuthSuccessful) {
                this.hasIntegrationUser = null;
            }

            GetIntegrationUser().then((response) => {
                if (response.Error) {
                    console.error("getIntegrationUser: ", response.Error);
                    return;
                }

                // Check if the user isn't found in Vin and create one
                if (response.Data?.user?.message == "User not found") {
                    if (!this.lastAuthSuccessful) {
                        this.hasIntegrationUser = false;
                        this.createUser = true;
                        this.dialog = this.renderDialog;
                    }
                    return;
                }

                if (response.Data?.user?.logins?.length > 0) {
                    this.hasIntegrationUser = true;
                    this.createUser = false;
                } else {
                    if (!this.lastAuthSuccessful) {
                        this.hasIntegrationUser = false;
                        this.createUser = true;
                        this.dialog = this.renderDialog;
                    }
                }
            });
        },
        getStepValue() {
            if (!this.authStatus) {
                return "Please wait while we log you in...";
            }

            const step = this.steps.find(step => step.key === this.authStatus);
            return step ? step.value : "Please wait while we log you in...";
        },
        handleLogin() {
            const options = {
                username: this.loginForm.username.trim() || "",
                password: this.loginForm.password.trim() || "",
            }

            this.isLoggingIn = true;

            // no longer need these toggled, we're now in the normalized login flow
            this.newMfaCodeRequired = false;
            this.timeoutOrError = false;
            this.sessionEnded = false;

            // If successful, we'll run off the webhooks rather than further responses from the integration app
            IntegrationsLogin(options).then((response) => {
                if (response.Data?.status !== 200 || response.Error) {
                    switch (response.Error) {
                        case "user is already logging into vin solutions":
                            // Do nothing, waiting for integration app to finish logging in
                            break;
                        default:
                            this.showSnackbar("error", "Something went wrong when logging you into Vin Solutions. Please contact Volie support if the problem persists.");
                            this.isLoggingIn = false;
                            break;
                    }
                }
            });
        },
        handleMfa() {
            const options = {
                mfa_code: this.loginForm.mfa_code.trim(),
            }

            this.isLoggingIn = true;
            this.authStatus = "mfa_submitted";
            this.loginForm.mfa_code = '';

            // Still running with webhooks, only worry about integration app responses if we get an error
            IntegrationsSubmitMfa(options).then((response) => {
                if (response.Data.status !== 200 || response.Error) {
                    let err = "Something went wrong, please try again later";
                    if (response.Error) {
                        err = response.Error;
                    }

                    this.showSnackbar("error", err);
                    this.isLoggingIn = false;
                }
            });
        },
        handleSubmit() {
            if (this.loginForm.mfa_code) {
                this.handleMfa();
            } else if (this.createUser) {
                this.createIntegrationUser();
            } else {
                this.handleLogin();
            }
        },
        enterWebhookLoginProgress(event) {
            this.authStatus = event;
            this.failedLogin = false;
            this.failedMfa = false;
            this.isLoggingIn = true;
            this.mfaRequired = false;
            this.sessionEnded = false;
            this.timeoutOrError = false;
        },
        onAuthEventNew(event) {
            // Verify response
            if (!event) {
                return;
            }

            switch(event) {
                case "auth.login_attempt":
                    this.enterWebhookLoginProgress(event);
                    break;
                case "auth.mfa_verification":
                    this.enterWebhookLoginProgress(event);
                    break;
                case "auth.mfa_required":
                    this.dialog = this.renderDialog;
                    this.isLoggingIn = false;
                    this.mfaRequired = true;
                    break;
                case "auth.failure":
                    this.resetLoginState();
                    this.createUser = true;
                    this.failedLogin = true;
                    break;
                case "auth.success":
                    this.authStatus = event; // TODO: a nice UX would be giving a green checkmark in the login component before closing, then we dont have to use snackbar
                    this.resetLoginState();
                    this.createUser = false;
                    this.hasIntegrationUser = true;

                    // temp bandaid to only show the success message if we went through the login process
                    if (this.dialog) {
                        this.dialog = false;
                        this.showSnackbar("success", "Login successful - Vin Solutions will receive actions from your account");
                    }
                    break;
                case "auth.timeout":
                    this.resetLoginState();
                    this.timeoutOrError = true;
                    break;
                case "auth.error":
                    this.resetLoginState();
                    this.timeoutOrError = true;
                    break;
                case "session.started":
                    this.createUser = false;
                    this.failedLogin = false;
                    this.hasIntegrationUser = true;
                    this.sessionEnded = false;
                    this.timeoutOrError = false;
                    break;
                case "session.ended":
                    this.resetLoginState();
                    if (this.hasIntegrationUser === true) {
                        this.createUser = false;
                    }
                    this.sessionEnded = true;
                    break;
                default:
                    console.warn("onAuthEventNew: Something unexpected happened: ", event);
                    break;
            }
        },
        resetLoginState() {
            this.failedLogin = false;
            this.failedMfa = false;
            this.isLoggingIn = false;
            this.mfaRequired = false;
            this.sessionEnded = false;
            this.timeoutOrError = false;
        },
        showSnackbar(color, text) {
            if (color?.length > 0 && text?.length > 0) {
                this.snackbar.color = color;
                this.snackbar.text = text;
                this.snackbar.show = true;
            }
        },
        updateVinLoginEnforcement(vinLoginEnforcement) {
            if (!vinLoginEnforcement) {
                return;
            }

            VolieState.store.commit('setCurrentUser', {
                ...this.currentUser,
                vin_login_enforcement: vinLoginEnforcement,
            });
        },
    },
    computed: {
        currentStepText() {
            if (!this.authStatus) {
                return "Logging you in...";
            }

            const step = this.steps.find(step => step.key === this.authStatus);
            return step ? step.value : "Logging you in...";
        },
        vinLoginEnforcement() {
            return this.currentUser?.vin_login_enforcement || {};
        },
        lastAuthSuccessful() {
            return this.vinLoginEnforcement.current_status === "auth_success";
        },
        lastAuthFailed() {
            return this.failedLogin || this.vinLoginEnforcement.current_status === "auth_failure";
        },
        credentialsRequired() {
            if (this.lastAuthSuccessful && !this.lastAuthFailed) {
                return false;
            }

            return this.createUser || this.lastAuthFailed || this.hasIntegrationUser === false;
        },
        hasActiveVinSession() {
            return this.vinLoginEnforcement.has_active_session === true || this.vinLoginStatus === "online";
        },
        ignoredEnforcementMessage() {
            if (this.vinLoginEnforcement?.friendly_acknowledged_at) {
                return `VinSolutions enforcement was ignored on ${this.vinLoginEnforcement.friendly_acknowledged_at}. VinSolutions activity must be entered manually until you reconnect.`;
            }

            return "VinSolutions enforcement has been ignored. VinSolutions activity must be entered manually until you reconnect.";
        },
        isCheckingIntegrationUser() {
            return !this.lastAuthSuccessful && this.hasIntegrationUser === null;
        },
        canReconnectWithStoredCredentials() {
            return !this.credentialsRequired && !this.hasActiveVinSession && (this.lastAuthSuccessful || this.hasIntegrationUser === true);
        },
        loginButtonDisabled() {
            let loginDisabled = true;

            if (!this.mfaRequired && this.canReconnectWithStoredCredentials) {
                loginDisabled = false;
            }

            if (!this.mfaRequired && this.credentialsRequired && this.loginForm.username !== "" && this.loginForm.password !== "") {
                loginDisabled = false;
            }

            if (this.mfaRequired && this.loginForm.mfa_code !== "") {
                loginDisabled = false;
            }

            if (this.newMfaCodeRequired) {
                loginDisabled = false;
            }

            if (this.isLoggingIn) {
                loginDisabled = true;
            }

            if (this.isCheckingIntegrationUser) {
                loginDisabled = true;
            }

            return loginDisabled;
        },
        loginButtonText() {
            return this.mfaRequired ? "Submit MFA" : "Login";
        },
        renderDialog() {
            return this.currentUser?.vin_login_enabled;
        },
        renderIgnoredEnforcementAlert() {
            return this.vinLoginEnforcement?.manual_entry_required === true || this.vinLoginEnforcement?.acknowledged === true;
        },
        renderLoginButton() {
            return !(this.vinLoginStatus == "unenabled" || this.vinLoginStatus == "online");
        },
        renderAcknowledgeButton() {
            const enforcement = this.currentUser?.vin_login_enforcement;
            return enforcement?.enabled === true && enforcement?.manual_entry_required !== true && this.vinLoginStatus === "offline";
        },
        vinLoginDecisionRequired() {
            return this.currentUser?.vin_login_enforcement?.requires_decision === true;
        }
    },
    components: {

    },
    props: {
        authEvent: String,
        currentUser: Object,
        vinLoginStatus: String,
    },
    watch: {
        authEvent(newVal) {
            if (newVal) {
                this.onAuthEventNew(newVal);
            }
        },
        vinLoginStatus(newVal) {
            if (newVal === "offline" && this.hasIntegrationUser === null) {
                this.getIntegrationUser();
            }
        }
    },
    created() {
        this.getIntegrationUser();
    },
    destroyed() {
    },
    mounted() {
    },
})
</script>

<style lang="scss" scoped>
    #app {
        .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
        }
        .word-break-normal {
            word-break: normal;
        }
        .vin-login-modal {
            word-break: normal;
            overflow-wrap: normal;
            white-space: normal;
        }
        .vin-login-body {
            padding-top: 12px;
        }
        .vin-login-copy {
            line-height: 1.5;
            margin-bottom: 12px;
            word-break: normal;
            overflow-wrap: normal;
            white-space: normal;
        }
        .vin-login-alert {
            margin-bottom: 16px;
            word-break: normal;
            overflow-wrap: normal;
            white-space: normal;
        }
        .vin-login-secondary-alert {
            margin-top: 16px;
            margin-bottom: 8px;
        }
        .vin-login-helper {
            color: rgba(0, 0, 0, 0.6);
            font-size: 0.8125rem;
            line-height: 1.4;
            margin-bottom: 16px;
            word-break: normal;
            overflow-wrap: normal;
            white-space: normal;
        }
        .vin-login-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            padding-top: 8px;

            .v-btn {
                margin-left: 0 !important;
            }
        }
        .vin-login-modal ::v-deep .v-alert__content {
            line-height: 1.45;
            word-break: normal;
            overflow-wrap: normal;
            white-space: normal;
        }
    }
</style>
